import { Box } from 'app/components/design-system-components'
import TextSpan from 'app/components/design-system-components/TextSpan'
import { Heading } from 'app/components/design-system-components/typography'
import theme from 'app/match/applicationReportPDF/assets/theme'
import { getPropertyArrangementValue } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/form.utils'
import type { RentalHistoryFormValues } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/types'
import DocumentList from 'app/shared_components/document_list/document_list'
import {
  RENTAL_LEDGER_OPTIONAL_DESCRIPTION,
  RENTAL_LEDGER_OPTIONAL_HEADER,
  RENTAL_LEDGER_REQUIRED_DESCRIPTION,
  RENTAL_LEDGER_REQUIRED_HEADER,
} from 'app/sm/apply/rental-ledger-required-policy.constants'
import { isRentalLedgerEligibleArrangement } from 'app/sm/apply/rental-ledger-required-policy.helpers'
import { DocumentTypes } from 'app/sm/helpers'
import { RENTAL_LEDGER_RENTER_LEARN_MORE_LINK } from 'app/utils/text/helpers'

// Simple device detection helper
const isMobile = () => window.innerWidth <= 768

interface DocumentSectionProps {
  values: RentalHistoryFormValues
  removeLedgerAttachment: (guidID: string) => void
  uploadLedgerAttachment: (documentType: string) => void
  isRentalLedgerRequiredByAgencyPolicy?: boolean
  isRentalLedgerRequiredForAddressByPolicy?: boolean
  isLoading?: boolean
}

const getLedgerHeader = (isRequired: boolean) =>
  isRequired ? RENTAL_LEDGER_REQUIRED_HEADER : RENTAL_LEDGER_OPTIONAL_HEADER

const getLedgerDescription = (isRequired: boolean) =>
  isRequired
    ? RENTAL_LEDGER_REQUIRED_DESCRIPTION
    : RENTAL_LEDGER_OPTIONAL_DESCRIPTION

export const DocumentSection = ({
  values,
  removeLedgerAttachment,
  uploadLedgerAttachment,
  isRentalLedgerRequiredByAgencyPolicy = false,
  isRentalLedgerRequiredForAddressByPolicy,
  isLoading = false,
}: DocumentSectionProps) => {
  const arrangementValue = getPropertyArrangementValue(
    values.propertyArrangement,
  )
  const isEligibleArrangement =
    isRentalLedgerEligibleArrangement(arrangementValue)
  const isRequiredByPolicyForAddress =
    isRentalLedgerRequiredForAddressByPolicy ??
    (values.isCurrentAddress === undefined
      ? true
      : values.isCurrentAddress && isEligibleArrangement)
  const shouldRenderRequiredLedgerState =
    isRentalLedgerRequiredByAgencyPolicy && isRequiredByPolicyForAddress

  const handleUploadDocument = (documentType: string) => {
    uploadLedgerAttachment(documentType)
  }

  const handleDeleteDocument = (attachmentId: string, event?: any) => {
    // Prevent any event bubbling that might cause form submission or navigation
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    // Use a wrapped function to ensure no additional parameters are passed
    removeLedgerAttachment(attachmentId)
  }

  return (
    <Box my={7}>
      <Heading level={3} displayingLevel={3} mb={4}>
        Rental Ledger{' '}
        <span style={isMobile() ? { fontSize: theme.fontSizes.pLarge16 } : {}}>
          {getLedgerHeader(shouldRenderRequiredLedgerState)}
        </span>
      </Heading>

      <Box mb={5}>
        <TextSpan>
          {getLedgerDescription(shouldRenderRequiredLedgerState)}{' '}
          <a
            href={RENTAL_LEDGER_RENTER_LEARN_MORE_LINK}
            target="_blank"
            rel="noreferrer"
          >
            Learn more
          </a>
        </TextSpan>
      </Box>

      <DocumentList
        showUploader={values.ledgerAttachments.length < 1}
        disableMultiple
        documents={values.ledgerAttachments}
        addDocument={handleUploadDocument}
        deleteDocument={handleDeleteDocument}
        documentType={DocumentTypes.rentalLedger}
        spinner={isLoading}
        fileType="RentalLedger"
        toggleDocumentsViewer={undefined}
        disabledUpload={isLoading}
        isFromAgencyBranding={false}
      />
    </Box>
  )
}
